import { DeleteOutlineOutlined, EditOutlined, ExpandMore } from '@mui/icons-material'
import { Grid } from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import CONSTANTS from '../../Constants'
import { getSubstring } from '../../Helpers/Util'
import ErrorSolidIcon from '../Icons/ErrorSolidIcon'
import InfoIcon from '../Icons/InfoIcon'
import ShieldIcon from '../Icons/ShieldIcon'
import SuccessIcon from '../Icons/SuccessIcon'
import ComplexTypeTable from '../Molecule/ComplexTypeTable/ComplextTypeTable'
import AssetViewMoreAndLessValue from '../Molecule/ViewMoreLess/AssetViewMoreAndLessValue'
import { ChipButton, MUIBox } from '../Shared'
import { TooltipText } from '../Shared/TooltipText'

interface ViewPageModal {
  view: any
  viewData: any[]
  pageName: string
  isEditable?: boolean
  onlyDetailView?: boolean
  showDeleteButton?: boolean
  editButtonRedirectionPath?: string
  onClickStatus?: (arg?: any) => void
  onClickDelete?: () => void
  secretButtonFunction?: () => void
  hideStatusButtons?: boolean
  tipsEditor?: React.ReactNode
}

const DetailViewRender: React.FC<ViewPageModal> = ({
  view,
  viewData = [],
  pageName = '',
  isEditable = true,
  onlyDetailView = false,
  showDeleteButton = false,
  editButtonRedirectionPath,
  onClickStatus,
  onClickDelete,
  secretButtonFunction = () => {
    return
  },
  hideStatusButtons = false,
  tipsEditor = null,
}: ViewPageModal) => {
  const navigate = useNavigate()
  const isControlPolicyPage =
    pageName === CONSTANTS.CONTROL_POLICY_VIEW_PAGE ||
    pageName === 'Tips Contents' ||
    pageName === 'bulk-state-change' ||
    pageName === 'interactive-bulk-update'

  const renderStatus = (value: string) => {
    if (value === 'ACTIVE') {
      return <ChipButton type='success' label='Active' style={{ padding: '0px 8px' }} />
    } else if (value === 'INACTIVE') {
      return <ChipButton type='secondary' label='In Active' style={{ padding: '0px 8px' }} />
    }
    return null
  }
  const renderCountValue = (item: any) => {
    if (typeof item?.value === 'string') {
      return (
        <AssetViewMoreAndLessValue
          value={item.value}
          fieldType={'TEXTAREA'}
          tagInput={false}
          dataType={'STRING'}
          element={{}}
        />
      )
    }
    if (Array.isArray(item?.value)) {
      return (
        <AssetViewMoreAndLessValue
          value={item.value}
          fieldType={'SELECT'}
          tagInput={false}
          dataType={'STRING'}
          element={{ attribute: { options: item.options } }}
        />
      )
    }
    return item?.value ?? CONSTANTS.DEFAULT_TEXT
  }
  const renderTitleValue = (item: any) => {
    if (typeof item?.value === 'string') {
      return getSubstring(item.value, 40) || CONSTANTS.DEFAULT_TEXT
    }
    if (Array.isArray(item?.value)) {
      return <TooltipText text={item.value.join(', ')} length={25} />
    }
    return item?.value || CONSTANTS.DEFAULT_TEXT
  }

  const renderValue = (item: any) => {
    if (item.isHeading) return ''
    if (item.label === 'Status') return renderStatus(item?.value)
    if (item?.isTitle) return renderTitleValue(item)
    if (item?.isWithCount) return renderCountValue(item)
    return item?.value ?? CONSTANTS.DEFAULT_TEXT
  }

  const getLabelClass = (item: any): string => {
    let labelClass = `sub-heading-6 view__role-list text-left ${
      isControlPolicyPage && !item.isHeading ? 'pl-control-policy-imp' : ''
    }`
    if (item.isHeading) {
      labelClass += ' font-w-600-imp'
    } else if (item.viewType !== 'complex') {
      labelClass += ' col34'
    }
    return labelClass
  }

  return (
    <MUIBox>
      {!onlyDetailView && (
        <>
          <div className='heading-6 d-flex text-left rolebox__header-padding'>
            <div className='d-flex align-items-center'>{pageName}</div>
            {isEditable && view?._id && (
              <div className='ml-auto d-flex'>
                {pageName === CONSTANTS.PARTNER_INFORMATION && (
                  <div className='mr-07'>
                    {view.emailVerified === false && (
                      <ChipButton
                        type='danger'
                        Icon={ErrorSolidIcon as any}
                        label='Verification Failed'
                      />
                    )}
                    {view.emailVerified === true && (
                      <ChipButton type='darkSuccess' Icon={SuccessIcon as any} label='Verified' />
                    )}
                    {view.emailVerified === null && (
                      <ChipButton type='info' Icon={InfoIcon as any} label='Verification Pending' />
                    )}
                  </div>
                )}
                {!hideStatusButtons && (
                  <div className='mr-07'>
                    <ChipButton
                      type={view?.status === 'ACTIVE' ? 'success' : 'secondary'}
                      dropdown
                      value={view?.status ?? 'ACTIVE'}
                      Icon={ExpandMore}
                      label={view?.status === 'ACTIVE' ? 'Active' : 'In Active'}
                      suffix
                      options={CONSTANTS.ROLE_STATUS}
                      action={onClickStatus}
                    />
                  </div>
                )}
                {showDeleteButton && (
                  <div className='mr-1' onClick={onClickDelete} data-testid='delete__chip-btn'>
                    <ChipButton type='danger' Icon={DeleteOutlineOutlined} label='Delete' />
                  </div>
                )}
                <div
                  onClick={() => navigate(editButtonRedirectionPath as string)}
                  data-testid='edit__chip-btn'
                >
                  <ChipButton type='primary' Icon={EditOutlined} label='Edit' />
                </div>
                {pageName === CONSTANTS.PARTNER_INFORMATION && (
                  <>
                    <div
                      className='ml-07'
                      onClick={() => secretButtonFunction()}
                      data-testid='secret__chip-btn'
                    >
                      <ChipButton
                        type={'darkPrimary'}
                        Icon={ShieldIcon as any}
                        label={'Issue New Token'}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className={'hr'}></div>
        </>
      )}
      {viewData.map((item: any, inx: number) => {
        const keyName = `${item?.label}_${inx}` // NOSONAR
        return (
          <div
            className={isControlPolicyPage && !item.isHeading ? 'p-12-inline' : ''}
            key={keyName}
          >
            <Grid container>
              <Grid
                item
                xs={item?.viewType === 'complex' ? 12 : 6}
                md={item?.viewType === 'complex' ? 12 : 4}
              >
                <div className={getLabelClass(item)}>{item?.label}</div>
              </Grid>
              {item?.viewType === 'complex' ? (
                <Grid item xs={12} md={12} className='view__role-list pt-0'>
                  <MUIBox>
                    <ComplexTypeTable columns={item.columns} tableRows={item.tableRows} />
                  </MUIBox>
                </Grid>
              ) : (
                <Grid item xs={6} md={8}>
                  <div
                    className={`sub-heading-6 view__role-list text-left col36 word-wrap p-view-page-imp${
                      isControlPolicyPage && !item.isHeading ? ' pr-control-policy-imp ' : ''
                    }`}
                  >
                    {renderValue(item)}
                  </div>
                </Grid>
              )}
            </Grid>
          </div>
        )
      })}
      {tipsEditor}
    </MUIBox>
  )
}

export default DetailViewRender
